// <emma-chat-config-badge> — mono pills showing what state Emma will
// inherit on the next refine. Port of monolith updateConfigBadge
// (index.html :3793-3831). Renders summary of `lastResolvedParams` —
// workload / GPU / providers / region / currency / price-unit / nodes —
// above the refine input so the user understands what gets carried over.
//
// Note: monolith currently hides this with CSS `display: none` on both
// the base class and `.is-visible` (effectively kill-switched in prod).
// The Lit version exposes a `?hidden` host attribute for parity; default
// is visible so it's easy to evaluate on the widget side.

import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { SearchParams } from '../types/search.js';
import { designTokens } from './design-tokens.js';
import { applyTooltipClamp } from './tooltip-clamp.js';

@customElement('emma-chat-config-badge')
export class EmmaChatConfigBadge extends LitElement {
  /** Resolved params from the most recent turn — drives the tag set. */
  @property({ attribute: false })
  params: SearchParams | null = null;

  static override styles = [
    designTokens,
    css`
      :host {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 6px;
        margin-bottom: 8px;
        padding: 0 2px;
        font-family: var(--font-mono);
        font-size: clamp(0.6875rem, 0.85vw, 0.75rem);
        line-height: 1.3;
        color: var(--gray-60);
      }
      :host([hidden]) {
        display: none;
      }
      .label {
        color: var(--gray-40);
        margin-right: 2px;
      }
      .tag {
        position: relative;
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 2px 8px;
        background: var(--gray-05);
        border: 1px solid var(--gray-10);
        border-radius: 6px;
        color: var(--gray-80);
        white-space: nowrap;
        /* QA-3: a long tag (e.g. a 9-provider exclusion list) must shrink to
           the row and ellipsize instead of blowing the badge row wide. The
           ellipsis lives on the INNER .tag-text — the .tag itself must stay
           overflow-visible so its tooltip ::after isn't clipped. */
        max-width: 100%;
        min-width: 0;
      }
      .tag-text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .tag--accent {
        background: var(--blue-03);
        border-color: var(--blue-15);
        color: var(--blue-accent);
      }

      /* QA-3 — tooltip with the FULL tag text, set on pointerover only when
         the .tag-text is actually truncated (see handleTagHover). Same
         data-tooltip pattern as the quick chips (QA #9), but positioned BELOW
         the tag: the badge row sits at the top of the refine zone, whose
         reveal animation clips upward overflow. */
      .tag[data-tooltip]::after {
        content: attr(data-tooltip);
        position: absolute;
        top: calc(100% + 6px);
        left: 50%;
        transform: translateX(-50%);
        max-width: var(--emma-tt-max, 260px);
        width: max-content;
        padding: 6px 10px;
        background: var(--gray-100);
        color: var(--white);
        font-family: var(--font-sans);
        font-size: 11px;
        font-weight: var(--font-medium);
        line-height: 1.4;
        border-radius: var(--radius-sm);
        white-space: normal;
        text-align: left;
        pointer-events: none;
        opacity: 0;
        z-index: 100;
        transition: opacity var(--duration-fast) var(--easing) 150ms;
      }
      .tag[data-tooltip][data-tt-align='right']::after {
        left: auto;
        right: 0;
        transform: none;
      }
      .tag[data-tooltip][data-tt-align='left']::after {
        left: 0;
        transform: none;
      }
      .tag[data-tooltip]:hover::after {
        opacity: 1;
      }
    `,
  ];

  /** Show the full-text tooltip only when the tag is actually truncated —
   *  measured live on hover (truncation depends on the panel width). */
  private handleTagHover(e: Event): void {
    const tag = e.currentTarget as HTMLElement;
    const text = tag.querySelector('.tag-text') as HTMLElement | null;
    if (!text) return;
    if (text.scrollWidth > text.clientWidth + 1) {
      tag.setAttribute('data-tooltip', text.textContent?.trim() ?? '');
      applyTooltipClamp(tag);
    } else {
      tag.removeAttribute('data-tooltip');
    }
  }

  override render() {
    const tags = this.params ? buildTags(this.params) : [];
    if (tags.length === 0) return nothing;
    return html`
      <span class="label">Current:</span>
      ${tags.map(
        (t) => html`
          <span
            class="tag${t.accent ? ' tag--accent' : ''}"
            @pointerover=${this.handleTagHover}
            ><span class="tag-text">${t.text}</span></span
          >
        `,
      )}
    `;
  }
}

interface Tag {
  text: string;
  accent?: boolean;
}

/** Build the tag list from resolved params. Mirrors monolith
 *  :3804-3823 — same field order and conditions — extended with the disk
 *  bracket tag (design-review fix 6). */
function buildTags(p: SearchParams): Tag[] {
  const out: Tag[] = [];
  if (p.workload) out.push({ text: p.workload.toUpperCase() });
  if (p.gpu) {
    const gpuTxt =
      (p.gpu_count && p.gpu_count > 1 ? `${p.gpu_count}x ` : '') + p.gpu;
    out.push({ text: gpuTxt, accent: true });
  }
  if (p.vcpu && !p.gpu) out.push({ text: `${p.vcpu} vCPU` });
  if (p.ram_gb && !p.gpu) out.push({ text: `${p.ram_gb} GB RAM` });
  const disk = formatDiskTag(p);
  if (disk) out.push({ text: disk });
  if (p.providers && p.providers.length > 0) {
    out.push({
      text: p.providers.length === 1 ? p.providers[0]! : p.providers.join(','),
    });
  }
  if (p.region) out.push({ text: p.region });
  if (p.currency && p.currency !== 'EUR') out.push({ text: p.currency });
  if (p.price_unit === 'month') out.push({ text: '/month' });
  if (p.node_min !== undefined && p.node_max !== undefined) {
    out.push({ text: `${p.node_min}–${p.node_max} nodes` });
  }
  return out;
}

/** Disk tag (design-review fix 6) — renders the resolved disk bracket the
 *  BFF pins from the compact shorthand ("16ssd" → exact 16 GB, "16ssd to
 *  60ssd" → 16–60 GB): exact → "16 GB SSD", range → "16–60 GB SSD",
 *  min-only (plain "16 GB disk" ≥-semantics) → "16+ GB SSD",
 *  type without size → "SSD". Empty string = no tag. */
export function formatDiskTag(p: SearchParams): string {
  const type = p.disk_type ? ` ${p.disk_type}` : '';
  if (p.disk_gb && p.disk_gb_max) {
    if (p.disk_gb === p.disk_gb_max) return `${p.disk_gb} GB${type}`;
    return `${p.disk_gb}–${p.disk_gb_max} GB${type}`;
  }
  if (p.disk_gb) return `${p.disk_gb}+ GB${type}`;
  if (p.disk_type) return p.disk_type;
  return '';
}

declare global {
  interface HTMLElementTagNameMap {
    'emma-chat-config-badge': EmmaChatConfigBadge;
  }
}
